<script setup lang="ts">
import { AkHeading3 } from '@ankorstore/design-system';
import { CustomRouterLink } from '@bc/shared';

defineProps<{
  product: {
    name: string;
    url: string;
    brandName: string;
    brandUrl: string;
  };
  isExternalLink?: boolean;
  withDrawer?: boolean;
  /** PRICE-2: tighten spacing (gap + hug the name) on the simplified tile. */
  simplified?: boolean;
}>();

defineEmits<{
  productClicked: [];
  brandClicked: [];
}>();
</script>
<template>
  <div
    class="card-product-names"
    :class="{ 'card-product-names--simplified': simplified }"
  >
    <div
      v-if="product.name && withDrawer"
      class="card-product-names__product-name card-product-names__product-name--drawer"
      @click="$emit('productClicked')"
    >
      <AkHeading3 class="card-product-names__product-name-text">
        {{ product.name }}
      </AkHeading3>
    </div>
    <CustomRouterLink
      v-else-if="product.name"
      :to="product.url"
      :url="product.url"
      :is-external-link="isExternalLink"
      @click="$emit('productClicked')"
    >
      <div class="card-product-names__product-name">
        <AkHeading3 class="card-product-names__product-name-text">
          {{ product.name }}
        </AkHeading3>
      </div>
    </CustomRouterLink>

    <CustomRouterLink
      v-if="product.brandName"
      :to="product.brandUrl"
      :url="product.brandUrl"
      :is-external-link="isExternalLink"
      @click="$emit('brandClicked')"
    >
      <span class="card-product-names__brand-name">
        {{ product.brandName }}
      </span>
    </CustomRouterLink>
  </div>
</template>

<style lang="scss" scoped>
@mixin lineClamp($lines) {
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: $lines;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-product-names {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  // PRICE-2 simplified tile: tighter name-to-brand gap.
  &--simplified {
    gap: 0.25rem;
  }

  &__product-name {
    display: flex;
    align-items: center;
    height: 2lh;

    &--drawer {
      cursor: pointer;
    }
  }

  // PRICE-2 simplified tile: name hugs its content instead of reserving 2 lines.
  &--simplified &__product-name {
    height: auto;
  }

  /* Increase specificity with & to override design system styles */
  & &__product-name-text {
    @apply ds-text-sm;
    font-weight: theme('fontWeight.semibold');
    @include lineClamp(2);
  }

  &__brand-name {
    @apply ds-text-sm;
    color: theme('colors.neutral.700');
    text-decoration: underline;
    @include lineClamp(1);
  }
}
</style>
